/**
 * @file ExportContentOptions.js
 * @version 1.0.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-16
 * @modified 2026-11-02
 * @description What goes INTO a table export — which rows, which columns, which
 *              totals, and whether each row's line details come with it. The
 *              companion to ExportLayoutOptions, which decides how it sits on
 *              the page, and drawn with the same chips so the two read as one
 *              set of settings.
 *
 *              Every choice defaults to "everything": all results rather than
 *              the page on screen, every column, every total, line details in.
 *              An export that quietly leaves something out is worse than one
 *              that has to be trimmed.
 *
 *              A group only appears when it would change something — no scope
 *              chips when there is a single page, no line-details switch when
 *              no row has lines, no totals chips when there is no totals row.
 *
 *              shapeExportPayload applies the choices to the payload a screen
 *              built. The preview and the PDF are both fed its result, so they
 *              cannot disagree about what was chosen.
 */

import React from 'react';
import './ExportLayoutOptions.css';

/**
 * The numeric totals a payload carries, as [{ key, label }] — the totals row's
 * keys that belong to a numeric column. The row's other keys are its label
 * ("12 invoices"), which is not a total and is never offered.
 */
export function totalKeysOf(payload) {
  if (!payload) return [];
  const numeric = new Map((payload.columns || []).filter(c => c.numeric).map(c => [c.key, c.label]));
  const keys = new Set();
  for (const t of payload.totals || []) {
    Object.keys(t || {}).forEach(k => { if (numeric.has(k)) keys.add(k); });
  }
  for (const g of payload.groups || []) {
    Object.keys(g?.subtotals || {}).forEach(k => { if (numeric.has(k)) keys.add(k); });
  }
  // A server-built export carries no rows or subtotals here, only which
  // figures it totals (Reports → Invoice Detail).
  for (const k of payload.totalKeys || []) { if (numeric.has(k)) keys.add(k); }
  return [...keys].map(key => ({ key, label: numeric.get(key) }));
}

/** True when any row, flat or grouped, carries line details. */
export function hasLineDetails(payload) {
  if (!payload) return false;
  const has = (r) => Array.isArray(r?.detail?.rows);
  return (payload.rows || []).some(has) || (payload.groups || []).some(g => (g.rows || []).some(has));
}

/**
 * The payload as the user has shaped it. Never mutates the snapshot: the modal
 * keeps the original so a column switched off can be switched back on.
 */
export function shapeExportPayload(payload, { hiddenColumns, hiddenTotals, includeLines }) {
  if (!payload) return payload;
  const columns = (payload.columns || []).filter(c => !hiddenColumns.has(c.key));
  // A column that is not shown has no total to show either.
  const dropped = new Set([...hiddenTotals, ...hiddenColumns]);
  const numericKeys = new Set(columns.filter(c => c.numeric).map(c => c.key));

  const row = (r) => {
    if (includeLines || !r || !r.detail) return r;
    const { detail, ...rest } = r;
    return rest;
  };
  const trim = (t) => {
    if (!t) return t;
    const out = { ...t };
    dropped.forEach(k => { delete out[k]; });
    return out;
  };
  // A totals row with every figure switched off is just its label, which reads
  // as a mistake — leave the row out.
  const stillHasFigures = (t) => Object.keys(t || {}).some(k => numericKeys.has(k));

  return {
    ...payload,
    columns,
    rows: (payload.rows || []).map(row),
    groups: (payload.groups || []).map(g => {
      const subtotals = g.subtotals ? trim(g.subtotals) : g.subtotals;
      return {
        ...g,
        rows: (g.rows || []).map(row),
        subtotals: subtotals && stillHasFigures(subtotals) ? subtotals : null,
      };
    }),
    totals: (payload.totals || []).map(trim).filter(stillHasFigures),
  };
}

function Chip({ active, icon, label, title, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`export-layout-fit${active ? ' active' : ''}`}
      title={title}
      disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
    >
      {icon && <span className="material-symbols-rounded">{icon}</span>}
      <span className="export-layout-fit-label">{label}</span>
    </button>
  );
}

function ExportContentOptions({
  payload,
  scope, onScope, available, scopeBusy,
  hiddenColumns, onToggleColumn,
  hiddenTotals, onToggleTotal,
  includeLines, onIncludeLines,
}) {
  if (!payload) return null;

  const columns = payload.columns || [];
  const totals = totalKeysOf(payload);
  const lines = hasLineDetails(payload);
  const showScope = !!available && Number(available.all) > Number(available.page);
  const visibleCount = columns.length - columns.filter(c => hiddenColumns.has(c.key)).length;

  return (
    <div className="export-layout export-content">
      <div className="export-layout-head">Contents</div>

      {showScope && (
        <div className="export-layout-line">
          <span className="export-layout-key">Rows</span>
          <div className="export-layout-fits">
            <Chip active={scope === 'all'} icon="select_all"
                  label={`All results (${Number(available.all).toLocaleString('en-GB')})`}
                  title="Every row that matches the search, whichever page it is on"
                  disabled={scopeBusy}
                  onClick={() => onScope('all')} />
            <Chip active={scope === 'page'} icon="article"
                  label={`This page (${Number(available.page).toLocaleString('en-GB')})`}
                  title="Only the rows on the page you are looking at"
                  disabled={scopeBusy}
                  onClick={() => onScope('page')} />
          </div>
        </div>
      )}

      {lines && (
        <div className="export-layout-line">
          <span className="export-layout-key">Line details</span>
          <div className="export-layout-fits">
            <Chip active={includeLines} icon="format_list_bulleted" label="Include"
                  title="Every row's lines, whether or not it is expanded on screen"
                  onClick={() => onIncludeLines(true)} />
            <Chip active={!includeLines} icon="remove" label="Leave out"
                  title="One line per row"
                  onClick={() => onIncludeLines(false)} />
          </div>
        </div>
      )}

      {totals.length > 0 && (
        <div className="export-layout-line">
          <span className="export-layout-key">Totals</span>
          <div className="export-layout-fits">
            {totals.map(t => (
              <Chip key={t.key}
                    active={!hiddenTotals.has(t.key) && !hiddenColumns.has(t.key)}
                    icon={hiddenTotals.has(t.key) ? 'check_box_outline_blank' : 'check_box'}
                    label={t.label}
                    disabled={hiddenColumns.has(t.key)}
                    title={hiddenColumns.has(t.key) ? 'This column is hidden' : `Show the ${t.label} total`}
                    onClick={() => onToggleTotal(t.key)} />
            ))}
          </div>
        </div>
      )}

      {columns.length > 1 && (
        <div className="export-layout-line">
          <span className="export-layout-key">Columns</span>
          <div className="export-layout-fits">
            {columns.map(c => {
              const on = !hiddenColumns.has(c.key);
              // The last visible column cannot be switched off — a table with
              // no columns is not an export.
              const locked = on && visibleCount <= 1;
              return (
                <Chip key={c.key}
                      active={on}
                      icon={on ? 'check_box' : 'check_box_outline_blank'}
                      label={c.label || c.key}
                      disabled={locked}
                      title={locked ? 'At least one column has to stay' : undefined}
                      onClick={() => onToggleColumn(c.key)} />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ExportContentOptions;
